import { fromEvent } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { CellNode, Column, DivRenderer } from './experimental.grid';

export interface Public {
    createWorkbook: (
        renderer: DivRenderer,
        getValue?: (cell: CellNode) => any,
        collapsable?: (column: Column) => boolean,
    ) => void;
    // To write a row before/after of the grid is possible to use these tree methods
    createWorkbookInitialization: () => void;
    createEmptyWorkbook: () => void;
    addWorksheetWithName: (name: string) => void;
    writeRow: (row: any[]) => void;
    writeGridWorkbook: (
        renderer: DivRenderer,
        getValue?: (cell: CellNode) => any,
        collapsable?: (column: Column) => boolean,
        columnsToCheckMerge?: number[],
    ) => void;
    serve: (filename: string) => Promise<void>;
    destroy: () => void;
}

interface Impl extends Public {
    // eslint-disable-next-line @typescript-eslint/no-misused-new
    new (): Impl;

    getBuffer: () => Promise<ArrayBuffer>;
}

// eslint-disable-next-line @typescript-eslint/promise-function-async
const loadModule = (): Promise<Impl> =>
    import(
        /* webpackChunkName: 'experimental.grid-export' */ './experimental.grid-export.impl'
        /* webpackPrefetch: true */
    ).then((impl) => new (impl.default as any as Impl)());

export class Exporter implements Public {
    private _impl: Impl;

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    createWorkbook(
        renderer: DivRenderer,
        getValue?: (cell: CellNode) => any,
        collapsable?: (column: Column) => boolean,
    ): Promise<void> {
        if (this._impl) {
            this._impl.destroy();
        }
        return loadModule().then((impl) => {
            this._impl = impl;
            this._impl.createWorkbook(renderer, getValue, collapsable);
        });
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    createEmptyWorkbook(): Promise<void> {
        if (this._impl) {
            this._impl.destroy();
        }

        return loadModule().then((impl) => {
            this._impl = impl;
            this._impl.createEmptyWorkbook();
        });
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    createWorkbookInitialization(): Promise<void> {
        if (this._impl) {
            this._impl.destroy();
        }

        return loadModule().then((impl) => {
            this._impl = impl;
            this._impl.createWorkbookInitialization();
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addWorksheetWithName(name: string) {
        if (!this._impl) {
            return;
        }

        this._impl.addWorksheetWithName(name);
    }

    writeGridWorkbook(
        renderer: DivRenderer,
        getValue?: (cell: CellNode) => any,
        collapsable?: (column: Column) => boolean,
        columnsToCheckMerge?: number[],
    ): void {
        if (!this._impl) {
            return;
        }

        this._impl.writeGridWorkbook(
            renderer,
            getValue,
            collapsable,
            columnsToCheckMerge,
        );
    }

    writeRow(row: any[]): void {
        if (!this._impl) {
            return;
        }

        this._impl.writeRow(row);
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    serve(filename: string): Promise<void> {
        if (!this._impl) return;
        return this._impl.getBuffer().then((buffer) => {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const link = document.createElement('a');
            if (window.navigator.msSaveOrOpenBlob) {
                fromEvent(link, 'click')
                    .pipe(
                        take(1),
                        tap(() => {
                            window.navigator.msSaveOrOpenBlob(blob, filename);
                        }),
                    )
                    .subscribe();
            } else {
                link.href = window.URL.createObjectURL(blob);
            }
            link.download = filename;
            link.click();
            if (!window.navigator.msSaveOrOpenBlob) {
                window.URL.revokeObjectURL(link.href);
            }
        });
    }

    destroy(): void {
        if (this._impl) {
            this._impl.destroy();
        }
    }
}
